import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
} from "@mui/material";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const [mode, setMode] = useState("signup");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    password: "",
  });

  useEffect(() => {
    if (token) {
      navigate("/");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (location.pathname.includes("login")) {
      setMode("login");
    } else {
      setMode("signup");
    }
  }, [location.pathname]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validate = () => {
    let isValid = true;
    const errors = { name: "", email: "", password: "" };

    if (mode === "signup") {
      if (!formData.name.trim()) {
        errors.name = "Name is required.";
        isValid = false;
      } else if (formData.name.length < 5) {
        errors.name = "Name must be at least 5 characters.";
        isValid = false;
      } else if (formData.name.length > 50) {
        errors.name = "Name must be less than 50 characters.";
        isValid = false;
      }
    }

    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
      isValid = false;
    } else if (!emailPattern.test(formData.email)) {
      errors.email = "Invalid email address.";
      isValid = false;
    } else if (formData.email.length > 100) {
      errors.email = "Email must be less than 100 characters.";
      isValid = false;
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required.";
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
      isValid = false;
    } else if (formData.password.length > 100) {
      errors.password = "Password must be less than 100 characters.";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!validate()) return;

    setIsSubmitting(true); // ✅ Disable button

    try {
      if (mode === "signup") {
        const { name, email, password } = formData;
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/welcome/signup`,
          { name, email, password }
        );
        if (window.gr) {
          window.gr("track", "conversion", { email });
        }
        setSuccessMessage(
          res.data.message || "Signup successful! Please verify your email."
        );
      } else {
        const { email, password } = formData;
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/welcome/login`,
          { email, password }
        );
        localStorage.setItem("token", res.data.token);
        setSuccessMessage("Login successful!");

        const preAuthState = JSON.parse(localStorage.getItem("preAuthState"));
        setTimeout(() => {
          if (preAuthState?.path && preAuthState.path !== "undefined") {
            window.location.href = `${preAuthState.path}${
              preAuthState.search || ""
            }`;
          } else {
            window.location.href = "/";
          }
        }, 1000);
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        (mode === "signup" ? "Signup failed." : "Login failed.");
      if (mode === "signup" && message.includes("User already exists")) {
        navigate("/welcome/login");
        setError("User already exists. Please login.");
      } else if (mode === "login" && message.includes("User not found")) {
        navigate("/welcome/signup");
        setError("User not found. Please sign up.");
      } else if (
        mode === "login" &&
        message.includes(
          "User already authenticated via Google. Please log in using Google"
        )
      ) {
        setError(
          "You have already signed up using Google Authentication. Please login with Google."
        );
      } else if (
        mode === "signup" &&
        message.includes(
          "User already authenticated via Google. Please log in using Google"
        )
      ) {
        setError(
          "You have already signed up using Google Authentication. Please login with Google."
        );
        navigate("/welcome/login");
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false); // ✅ Re-enable button
    }
  };

  const handleGoogleSignup = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/auth/google`;
  };

  const handleToggleMode = () => {
    if (mode === "signup") {
      navigate("/welcome/login");
    } else {
      navigate("/welcome/signup");
    }
    setError("");
    setSuccessMessage("");
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 3, mb: 5 }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h5" align="center" mb={1}>
          {mode === "signup"
            ? "Sign Up in Just Seconds"
            : "Login to MyFreshTools"}
        </Typography>

        <>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={
              <img
                src="https://d2ebde4f326d7eaf36c8632657bdafc6.cdn.bubble.io/cdn-cgi/image/w=48,h=48,f=auto,dpr=2,fit=contain/f1687521899401x230190343801164770/google.webp"
                alt="Google"
                style={{ width: 24, height: 24, marginRight: 8 }}
              />
            }
            onClick={handleGoogleSignup}
          >
            {mode === "signup" ? "Continue with Google" : "Login with Google"}
          </Button>
          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 1, color: "text.secondary", fontWeight: "bold" }}
          >
            {mode === "signup"
              ? "It's faster! ⚡ Sign up with Google in just one click."
              : "Quick login — no password needed with Google."}
          </Typography>
          <Divider sx={{ my: 1 }}>OR</Divider>
        </>

        {mode === "signup" && (
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
        )}

        <TextField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          fullWidth
          error={!!formErrors.email}
          helperText={formErrors.email}
        />

        <TextField
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          fullWidth
          error={!!formErrors.password}
          helperText={formErrors.password}
        />

        {error && (
          <Typography color="error" align="center">
            {error}
          </Typography>
        )}

        {successMessage && (
          <Box
            sx={{
              backgroundColor: "#e0f7fa",
              border: "1px solid #4dd0e1",
              padding: 1,
              borderRadius: 2,
              marginTop: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              {successMessage}
            </Typography>
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mb: 0.5 }}
          disabled={isSubmitting} // ✅ Disable while submitting
        >
          {isSubmitting
            ? "Please wait..."
            : mode === "signup"
            ? "Sign Up"
            : "Login"}
        </Button>

        <Button
          type="button"
          onClick={handleToggleMode}
          sx={{ mt: 0, mb: 0, textTransform: "none", minHeight: 0, padding: 0 }}
        >
          {mode === "signup"
            ? "Already have an account? Login"
            : "Don't have an account? Sign Up"}
        </Button>

        {mode === "login" && (
          <Button
            type="button"
            onClick={() => navigate("/forgot-password")}
            sx={{ textTransform: "none" }}
          >
            Forgot password?
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default AuthPage;
